import { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Multi-photo uploader for authenticated user submissions (e.g. reviews).
 * - PNG/JPEG/WEBP/GIF up to 2 MB each
 * - Calls onChange(urls) every time the list of uploaded URLs changes
 * - Caps the total to `max` photos (default 3)
 */
export default function PhotoUploader({
  urls = [],
  onChange,
  max = 3,
  endpoint = "/uploads/review-photo",
  label,
  testId = "photo-uploader",
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const remaining = Math.max(0, max - urls.length);

  const upload = async (files) => {
    if (!files?.length) return;
    if (remaining === 0) {
      toast.error(`Maximum ${max} photos`);
      return;
    }
    setUploading(true);
    try {
      const toUpload = Array.from(files).slice(0, remaining);
      const newUrls = [];
      for (const file of toUpload) {
        if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
          toast.error(`${file.name}: format non supporté`);
          continue;
        }
        if (file.size > 2 * 1024 * 1024) {
          toast.error(`${file.name}: image trop lourde (max 2 Mo)`);
          continue;
        }
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        try {
          const r = await api.post(endpoint, { data: dataUrl, filename: file.name });
          newUrls.push(r.data.url);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Échec : ${file.name}`);
        }
      }
      if (newUrls.length) {
        onChange?.([...urls, ...newUrls]);
        toast.success(`${newUrls.length} photo${newUrls.length > 1 ? "s" : ""} ajoutée${newUrls.length > 1 ? "s" : ""}`);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => onChange?.(urls.filter((_, i) => i !== idx));

  return (
    <div data-testid={testId}>
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {urls.map((u, i) => (
            <div key={`${u}-${i}`} className="relative" data-testid={`photo-thumb-${i}`}>
              <img
                src={u}
                alt=""
                className="w-16 h-16 object-cover border border-white/20"
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute -top-2 -right-2 bg-white text-[#001F3F] rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-500 hover:text-white transition"
                aria-label="Retirer la photo"
                data-testid={`photo-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {remaining > 0 && (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 border border-white/30 text-white/70 hover:border-white hover:text-white transition px-4 py-2 text-xs uppercase tracking-widest font-bold disabled:opacity-50"
          data-testid={`${testId}-trigger`}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          {uploading
            ? "Envoi…"
            : label || `Ajouter une photo (${remaining} restante${remaining > 1 ? "s" : ""})`}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => upload(e.target.files)}
      />
    </div>
  );
}
